import React from 'react';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import { aboutData, teamData } from '../mock';
import { Instagram } from 'lucide-react';

const About = () => {
  return (
    <>
      <SEO
        title="About Frames by Gaurav | Lucknow Photography Studio"
        description="Meet Gaurav Vishwakarma and learn about Frames by Gaurav, a Lucknow photography studio for weddings, events, portraits and commercial projects."
        keywords="about frames by gaurav, gaurav vishwakarma photographer, photography studio lucknow, professional photography team, wedding photographer team lucknow"
        canonicalUrl="/about"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: 'About - Frames by Gaurav',
          description:
            'Learn about our photography studio, our founder, and our passionate team of photographers.',
          url: 'https://www.framesbygaurav.com/about',
        }}
      />
      <Layout>
        {/* Hero Section */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <h1 className="text-5xl md:text-6xl font-bold text-center mb-6 tracking-tight">
              {aboutData.title}
            </h1>
            <p className="text-2xl text-center text-gray-600 mb-16 italic">
              {aboutData.tagline}
            </p>

            {/* Story */}
            <div className="prose prose-lg max-w-none mb-16">
              {aboutData.story.split('\n\n').map((paragraph, index) => (
                <p
                  key={index}
                  className="text-gray-700 leading-relaxed mb-6 text-lg"
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </section>

        {/* Meet the Founder Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 tracking-tight">
              Meet The Founder
            </h2>
            <div className="flex flex-col md:flex-row items-center gap-12">
              {/* Founder Image */}
              <div className="w-full md:w-1/2 flex justify-center">
                <div className="w-full max-w-md aspect-[3/4] overflow-hidden">
                  <img
                    src={teamData[0].image}
                    alt={teamData[0].name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>

              {/* Founder Info */}
              <div className="w-full md:w-1/2 flex flex-col justify-center text-center md:text-center">
                <h3 className="text-3xl md:text-4xl font-bold mb-2 tracking-tight">
                  {teamData[0].name}
                </h3>
                <p className="text-xl text-gray-600 mb-6 italic">
                  {teamData[0].role}
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  {teamData[0].bio}
                </p>

                {/* Instagram Link */}
                <div className="flex justify-center">
                  <a
                    href={teamData[0].instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-gray-700 hover:text-black transition-colors"
                  >
                    <Instagram size={24} />
                    <span className="font-semibold">Follow on Instagram</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Meet the Co-Founder Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center gap-10">
            <div
              className="w-full max-w-md aspect-[3/4] flex items-center justify-center bg-gray-100 text-gray-800"
              aria-label="Ajayraj Verma initials"
            >
              <span className="text-7xl md:text-8xl font-bold">AV</span>
            </div>
            <div className="w-full md:flex-1 text-center md:text-left">
              <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">
                Co-founder
              </p>
              <h2 className="text-3xl md:text-4xl font-bold mb-3 tracking-tight">
                Ajayraj Verma
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Co-founder of Frames by Gaurav. A fuller profile will be added
                soon.
              </p>
            </div>
          </div>
        </section>

        {/* From the Founder Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-16 tracking-tight">
              From the Founder
            </h2>
            <div className="relative max-w-2xl mx-auto">
              <div
                className="relative w-full"
                style={{ paddingBottom: '56.25%', height: 0 }}
              >
                <iframe
                  src="https://www.youtube.com/embed/S7F5-Yi4czU"
                  title="From the Founder"
                  className="absolute top-0 left-0 w-full h-full rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        </section>

        {/* Meet Our Team Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 tracking-tight">
              Meet Our Team
            </h2>
            <div className="space-y-20">
              {teamData.map((member, index) => (
                <div
                  key={member.id}
                  className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} items-center gap-12`}
                >
                  {/* Team Member Image */}
                  <div className="w-full md:w-1/2 flex justify-center">
                    <div className="w-full max-w-md aspect-[3/4] overflow-hidden">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                  </div>

                  {/* Team Member Info */}
                  <div className="w-full md:w-1/2 flex flex-col justify-center text-center md:text-center">
                    <h3 className="text-3xl md:text-4xl font-bold mb-2 tracking-tight">
                      {member.name}
                    </h3>
                    <p className="text-xl text-gray-600 mb-6 italic">
                      {member.role}
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-6">
                      {member.bio}
                    </p>

                    {/* Instagram Link */}
                    <div className="flex justify-center">
                      <a
                        href={member.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-gray-700 hover:text-black transition-colors"
                      >
                        <Instagram size={24} />
                        <span className="font-semibold">
                          Follow on Instagram
                        </span>
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Video Editor Section */}
        <section className="py-20 bg-white">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="w-full md:w-1/2 flex justify-center">
              <div className="w-full max-w-md aspect-[3/4] overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/40012016/pexels-photo-40012016.jpeg"
                  alt="Video Editor"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
            </div>

            <div className="w-full md:w-1/2 flex flex-col justify-center text-center md:text-center">
              <h3 className="text-3xl md:text-4xl font-bold mb-2 tracking-tight">
                Avanish Keshari
              </h3>
              <p className="text-xl text-gray-600 mb-6 italic">Video Editor</p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Avanish Keshari is a professional Video Editor with 1 year of
                experience in the photography and cinematography industry. He
                specializes in editing wedding films, cinematic highlights,
                social media reels, event videos, and promotional content. With
                a strong understanding of pacing, music, transitions, color, and
                visual storytelling, Avanish transforms raw footage into
                engaging and polished visual stories. His creative approach and
                attention to detail help ensure that every project reflects the
                emotion, quality, and vision of the studio.
              </p>

              <div className="flex justify-center">
                <a
                  href="https://www.instagram.com/iamavanishkeshari_"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-gray-700 hover:text-black transition-colors"
                >
                  <Instagram size={24} />
                  <span className="font-semibold">Follow on Instagram</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Image Section */}
        {/* <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="aspect-[3/4] overflow-hidden">
              <img
                src="https://images.pexels.com/photos/40012016/pexels-photo-40012016.jpeg"
                alt="Photography equipment"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="aspect-[3/4] overflow-hidden">
              <img
                src="https://images.pexels.com/photos/35697247/pexels-photo-35697247.jpeg"
                alt="Photographer at work"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
          </div>
        </div>
      </section> */}
      </Layout>
    </>
  );
};

export default About;
